import { ParamsType, RCXAdapter, RCXLogger, RecordType } from 'rcx_adapter';
import i18n from 'rcx_i18n';

import {
    getEntityConfigs,
    fieldMap,
    EntityConfigKey,
    nameEntitiesConfigs,
    relatedToEntitiesConfigs,
    nameEntitiesConfigsIncident,
} from './entityConfigs';
import { LogAdapter } from './LogAdapter';
import {
    ServiceNowAdapterEventEmitter,
    type AdapterConfig,
    type ServiceNowAdapterOptions,
    type ServiceNowRecordItem,
    type Configuration,
} from './ServiceNowAdapter.interface';
import { StateSyncAdapter } from './StateSyncAdapter';
import Config from '../../config/app.config';
import { CALL_LOGGING_TYPE_KEY, LOGGING_TYPE } from '../constants';
import { ServiceNowAdapterEvent, ServiceNowEntity } from '../enums';
import type { RingCXForServiceNowConfig } from '../interfaces';
import { Authorize } from '../lib/Authorize';
import { DataFetch } from '../lib/DataFetch';
import { Token } from '../lib/Token';
import { gotoSNAuthorizePage } from '../utils';
import { catchError } from '../utils/catchError';
import ExecutionQueue from '../utils/ExecutionQueue';
import { getParameterByName } from '../utils/url';

declare const openFrameAPI: any;

export const RCXCtiConfig = {
    frameSrc: Config.agentUIUrl,
    id: `ringcx-cti`,
};

class ServiceNowAdapter extends ServiceNowAdapterEventEmitter {
    config: AdapterConfig;

    global: Window & typeof globalThis;

    storage?: Storage;

    private _openFrameInitPromise: Promise<void> | null = null;
    private _queue: ExecutionQueue;
    public configuration: Configuration = {
        clientId: '',
        clientSecret: '',
        domain: '',
    };

    private _dataFetch: DataFetch;
    private _rcxAdapter: RCXAdapter;
    private _locale: string = navigator.language;
    private _stateSyncAdapter: StateSyncAdapter;

    private _ringCXForServiceNowConfig: RingCXForServiceNowConfig[] = [];

    private _logAdapter: LogAdapter;

    constructor({ config, storage, global = window, openFrameInitCallback }: ServiceNowAdapterOptions) {
        super();
        this.config = config;
        this.storage = storage;
        this.global = global;
        this._openFrameInitPromise = null;
        this._queue = new ExecutionQueue();
        this._dataFetch = undefined as any;
        this._rcxAdapter = RCXAdapter.instance();
        this._locale = navigator.language;
        i18n.init({
            locale: this._locale,
            appName: 'servicenow',
        });

        this.openFrameInit(
            () => {
                RCXLogger.getInstance().info('ServiceNow openFrameInit success');
                openFrameInitCallback?.();
                this._dataFetch = new DataFetch({
                    domain: this.configuration.domain,
                });
                this._logAdapter = new LogAdapter(this, this._dataFetch);
                this._stateSyncAdapter = new StateSyncAdapter(
                    this._dataFetch,
                    this._rcxAdapter,
                    this.configuration.domain
                );
                this.onAgentPresenceStateChange((data) => {
                    this._stateSyncAdapter.onAgentPresenceStateChange(data);
                });
            },
            () => {
                RCXLogger.getInstance().info('ServiceNow openFrameInit failure');
                openFrameInitCallback?.();
            },
            () => {}
        ).catch((error) => {
            // @ts-ignore
            window[Symbol.for('rcx_sentry')]?.captureException({ error });
        });

        this.onClickToDial((...arg) => {
            this.emit(ServiceNowAdapterEvent.CLICK_TO_DIAL, arg);
        });
    }

    private onAgentPresenceStateChange(callback: (data: any) => void) {
        this.snPresentOrCall(
            () => {},
            () => {
                openFrameAPI.subscribe(openFrameAPI.EVENTS.OPENFRAME_AWA_AGENT_PRESENCE, callback);
            }
        );
    }

    private snInstance() {
        return typeof window.SNC === 'undefined' ? null : window.SNC;
    }

    private snPresentOrCall(callback: () => void, snCall: () => void) {
        const sn = this.snInstance();
        if (sn !== null) {
            snCall();
        } else {
            RCXLogger.getInstance().info('SN interaction is not available. Trying to wait for it to load...');
            this._queue.add(() => {
                const sn2 = this.snInstance();
                if (sn2 !== null) {
                    snCall();
                    // We don't need the _queue any more as now the interaction script is loaded
                    this._queue.off();
                    // task is successfully executed
                    return true;
                } else {
                    // the task need to be re-executed
                    return false;
                }
            });
        }
    }

    private openFrameInit(initSuccess: () => void, initFailure: () => void, callback: () => void) {
        if (this._openFrameInitPromise) {
            return this._openFrameInitPromise;
        }
        this._openFrameInitPromise = new Promise((resolve, reject) => {
            this.snPresentOrCall(callback, () => {
                RCXLogger.getInstance().info('ServiceNow openFrameInit');
                openFrameAPI.init(
                    this.config,
                    (params: any) => {
                        this.configuration = JSON.parse(params.configuration || '{}');
                        if (this.configuration.domain && !this.configuration.domain.endsWith('/')) {
                            this.configuration.domain += '/';
                        }
                        localStorage.setItem('snClientId', this.configuration.clientId);
                        localStorage.setItem('snClientSecret', this.configuration.clientSecret);
                        localStorage.setItem('snOrigin', this.configuration.domain);
                        initSuccess();
                        resolve();
                    },
                    (error: any) => {
                        initFailure();
                        reject(error);
                    }
                );
            });
        });
        return this._openFrameInitPromise;
    }

    private onClickToDial(callback: () => void) {
        this.snPresentOrCall(
            () => {},
            () => {
                openFrameAPI.subscribe(openFrameAPI.EVENTS.COMMUNICATION_EVENT, callback);
            }
        );
    }

    private async searchEntities(
        requests: Array<{ type: EntityConfigKey; params: { [key: string]: any } }>,
        paramsType?: ParamsType
    ) {
        const results = await Promise.allSettled(
            requests.map(({ type, params }) => this._dataFetch.getTableData(type as RecordType, params))
        );

        results.forEach((result) => {
            if (result.status === 'rejected') {
                // @ts-ignore
                window[Symbol.for('rcx_sentry')]?.captureException({ error: result.reason });
            }
        });

        return results.reduce((acc: ServiceNowRecordItem[], result, index) => {
            if (result.status === 'fulfilled' && result.value.result) {
                const entityType = requests[index].type;
                const config = getEntityConfigs(
                    paramsType === ParamsType.CALL && this.getCallLoggingType() === LOGGING_TYPE.INCIDENT
                )[entityType as EntityConfigKey];

                const items = result.value.result.map((item: any) => ({
                    ...item,
                    id: item.sys_id,
                    type:
                        entityType === RecordType.USER
                            ? item.sys_class_name === 'customer_contact'
                                ? RecordType.CONTACT
                                : RecordType.USER
                            : entityType,
                    name: item[config!.nameField],
                    url: `${this.configuration.domain}/nav_to.do?uri=${config!.urlPath}.do?sys_id=${item.sys_id}`,
                    ...(config!.extraProps || {}),
                }));

                return [...acc, ...items];
            }
            return acc;
        }, []);
    }

    // --------------------- public methods ---------------------
    public async authorize() {
        const _url = window.location.href;
        const code = getParameterByName(_url, 'code');
        const token = new Token();
        if (code) {
            // remove agentUIUrl after back from authorize page
            localStorage.removeItem('agentUIUrl');
            // delete the code from the url
            history.pushState({}, document.title, Config.adapterUrl);
            const authorize = new Authorize({
                snOrigin: this.configuration.domain,
                clientId: this.configuration.clientId,
                code: code,
                clientSecret: this.configuration.clientSecret,
            });
            try {
                const response: any = await authorize._tokenRequest({});
                this.emit(ServiceNowAdapterEvent.UPDATE_IS_LOADING, false);
                token.setData(response);
                return { success: true };
            } catch (e) {
                this.emit(ServiceNowAdapterEvent.UPDATE_IS_LOADING, false);
                this.emit(ServiceNowAdapterEvent.UPDATE_IS_ERROR, true);
                token.setData(null); // Provide an argument here
                return { success: false, error: e };
            }
        } else if (!token.accessTokenValid()) {
            gotoSNAuthorizePage(this.configuration.domain, this.configuration.clientId);
            return { success: false, error: new Error('Authorization required') };
        } else {
            // remove agentUIUrl because the user has already authorized
            localStorage.removeItem('agentUIUrl');
            return { success: true };
        }
    }

    public openFrameOpenServiceNowFormBySysId(id: string, entity: string) {
        openFrameAPI.openServiceNowForm({
            query: `sys_id=${id}`,
            entity,
        });
    }

    public openFrameOpenServiceNowForm(entity: ServiceNowEntity, query: string) {
        openFrameAPI.openServiceNowForm({
            entity,
            query,
        });
    }

    public async getStandMatch(
        value: string,
        field: keyof typeof fieldMap,
        crmEntityType: string[] = [],
        type?: ParamsType
    ) {
        const entitiesToQuery =
            crmEntityType.length === 0 || crmEntityType.includes('*')
                ? Object.keys(
                      getEntityConfigs(this.getCallLoggingType() === LOGGING_TYPE.INCIDENT && type === ParamsType.CALL)
                  )
                : crmEntityType;

        const requests = entitiesToQuery.map((entityType) => ({
            type: entityType as EntityConfigKey,
            params: { [fieldMap[field]?.[entityType as RecordType] || field]: value },
        }));

        return this.searchEntities(requests, type);
    }

    public async getCustomMatch(type: string, fields: any, paramsType: ParamsType) {
        const entitiesToQuery =
            type === 'name'
                ? paramsType === ParamsType.CALL && this.getCallLoggingType() === LOGGING_TYPE.INCIDENT
                    ? nameEntitiesConfigsIncident
                    : nameEntitiesConfigs
                : relatedToEntitiesConfigs;
        const types = Object.keys(entitiesToQuery);

        const requests = types.map((entityType) => ({
            type: entityType as EntityConfigKey,
            params: fields,
        }));

        return this.searchEntities(requests, paramsType);
    }

    public showOpenFrame() {
        openFrameAPI.show();
    }

    public setFrameWidth(width: number) {
        openFrameAPI.setWidth(width);
    }

    public signOutAgent() {
        this._rcxAdapter.signOutAgent();
    }

    public async getVersion() {
        const { result = [] } = await this._dataFetch.getVersion();
        if (result.length > 0) {
            return result[0].value?.match(/^(.*?-\d{2}-\d{2}-\d{4}.*?)-\d{2}-\d{2}-\d{4}/)?.[1];
        } else {
            return '';
        }
    }

    @catchError
    public async getRingCXForServiceNowConfig() {
        const { result = [] } = await this._dataFetch.getRingCXForServiceNowConfig();
        this._ringCXForServiceNowConfig = result.map((item: { u_key: string; u_value: string }) => ({
            key: item.u_key,
            value: item.u_value,
        }));
    }

    public getCallLoggingType(): LOGGING_TYPE {
        const rawLoggingType = this._ringCXForServiceNowConfig.find((item) => item.key === CALL_LOGGING_TYPE_KEY);
        if (rawLoggingType) {
            return rawLoggingType.value as LOGGING_TYPE;
        }
        return LOGGING_TYPE.INTERACTION;
    }

    public getLogAdapter() {
        return this._logAdapter;
    }

    public getStateSyncAdapter() {
        return this._stateSyncAdapter;
    }
}

export { ServiceNowAdapter };
